import React, { useState, useEffect } from 'react';
import { 
  Settings,
  Bell,
  Shield,
  CreditCard,
  Truck,
  Store,
  Mail,
  Globe,
  Palette,
  Database,
  Key,
  Clock,
  DollarSign,
  Percent,
  Save,
  RefreshCcw,
  Check,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';

interface SettingsSection {
  id: string;
  label: string;
  icon: React.ElementType;
}

const settingsSections: SettingsSection[] = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'delivery', label: 'Delivery', icon: Truck },
  { id: 'business', label: 'Business', icon: Store },
  { id: 'security', label: 'Security', icon: Shield },
];

interface ToggleProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  label: string;
  description?: string;
}

function Toggle({ enabled, onChange, label, description }: ToggleProps) {
  return (
    <div className="flex items-center justify-between py-2 sm:py-3 gap-3">
      <div className="min-w-0">
        <p className="font-medium text-heading text-sm sm:text-base">{label}</p>
        {description && <p className="text-xs sm:text-sm text-body mt-0.5 line-clamp-2">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={cn(
          "relative w-10 h-5 sm:w-12 sm:h-6 rounded-full transition-colors flex-shrink-0",
          enabled ? "bg-primary" : "bg-gray-300"
        )}
      >
        <span className={cn(
          "absolute top-0.5 sm:top-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm",
          enabled ? "translate-x-5 sm:translate-x-7" : "translate-x-0.5 sm:translate-x-1"
        )} />
      </button>
    </div>
  );
}

export function AdminSettings() {
  const [activeSection, setActiveSection] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  // General Settings
  const [platformName, setPlatformName] = useState('Fash');
  const [supportEmail, setSupportEmail] = useState('support@fash.com');
  const [timezone, setTimezone] = useState('America/New_York');

  // Notification Settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [orderAlerts, setOrderAlerts] = useState(true);
  const [disputeAlerts, setDisputeAlerts] = useState(true);

  // Payment Settings
  const [platformFee, setPlatformFee] = useState('15');
  const [driverCommission, setDriverCommission] = useState('80');
  const [minOrderAmount, setMinOrderAmount] = useState('25');
  const [autoPayouts, setAutoPayouts] = useState(true);
  const [payoutSchedule, setPayoutSchedule] = useState('weekly');

  // Delivery Settings
  const [defaultDeliveryFee, setDefaultDeliveryFee] = useState('9.99');
  const [freeDeliveryThreshold, setFreeDeliveryThreshold] = useState('100');
  const [maxDeliveryRadius, setMaxDeliveryRadius] = useState('10');
  const [estimatedDeliveryTime, setEstimatedDeliveryTime] = useState('60');

  // Business Settings
  const [requireApproval, setRequireApproval] = useState(true);
  const [allowSelfOnboarding, setAllowSelfOnboarding] = useState(true);
  const [commissionRate, setCommissionRate] = useState('12');
  const [autoAcceptOrders, setAutoAcceptOrders] = useState(false);

  // Security Settings
  const [twoFactorRequired, setTwoFactorRequired] = useState(true);
  const [sessionTimeout, setSessionTimeout] = useState('30');
  const [ipWhitelist, setIpWhitelist] = useState(false);
  const [auditLogging, setAuditLogging] = useState(true);

  // Load saved settings from admin_config (general section)
  useEffect(() => {
    const load = async () => {
      try {
        const getConfig = httpsCallable(functions, 'getAdminConfig');
        const result: any = await getConfig({});
        const general = result?.data?.config?.general;
        if (general) {
          if (general.platformName) setPlatformName(general.platformName);
          if (general.supportEmail) setSupportEmail(general.supportEmail);
          if (general.timezone) setTimezone(general.timezone);
          if (general.emailNotifications !== undefined) setEmailNotifications(!!general.emailNotifications);
          if (general.pushNotifications !== undefined) setPushNotifications(!!general.pushNotifications);
          if (general.smsNotifications !== undefined) setSmsNotifications(!!general.smsNotifications);
          if (general.orderAlerts !== undefined) setOrderAlerts(!!general.orderAlerts);
          if (general.disputeAlerts !== undefined) setDisputeAlerts(!!general.disputeAlerts);
          if (general.platformFee !== undefined) setPlatformFee(String(general.platformFee));
          if (general.driverCommission !== undefined) setDriverCommission(String(general.driverCommission));
          if (general.minOrderAmount !== undefined) setMinOrderAmount(String(general.minOrderAmount));
          if (general.autoPayouts !== undefined) setAutoPayouts(!!general.autoPayouts);
          if (general.payoutSchedule) setPayoutSchedule(general.payoutSchedule);
          if (general.defaultDeliveryFee !== undefined) setDefaultDeliveryFee(String(general.defaultDeliveryFee));
          if (general.freeDeliveryThreshold !== undefined) setFreeDeliveryThreshold(String(general.freeDeliveryThreshold));
          if (general.maxDeliveryRadius !== undefined) setMaxDeliveryRadius(String(general.maxDeliveryRadius));
          if (general.estimatedDeliveryTime !== undefined) setEstimatedDeliveryTime(String(general.estimatedDeliveryTime));
          if (general.requireApproval !== undefined) setRequireApproval(!!general.requireApproval);
          if (general.allowSelfOnboarding !== undefined) setAllowSelfOnboarding(!!general.allowSelfOnboarding);
          if (general.commissionRate !== undefined) setCommissionRate(String(general.commissionRate));
          if (general.autoAcceptOrders !== undefined) setAutoAcceptOrders(!!general.autoAcceptOrders);
          if (general.twoFactorRequired !== undefined) setTwoFactorRequired(!!general.twoFactorRequired);
          if (general.sessionTimeout !== undefined) setSessionTimeout(String(general.sessionTimeout));
          if (general.ipWhitelist !== undefined) setIpWhitelist(!!general.ipWhitelist);
          if (general.auditLogging !== undefined) setAuditLogging(!!general.auditLogging);
        }
      } catch (err: any) {
        console.error('[AdminSettings] failed to load config:', err?.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      const saveConfig = httpsCallable(functions, 'saveAdminConfig');
      await saveConfig({
        section: 'general',
        config: {
          platformName,
          supportEmail,
          timezone,
          emailNotifications,
          pushNotifications,
          smsNotifications,
          orderAlerts,
          disputeAlerts,
          platformFee: parseFloat(platformFee) || 0,
          driverCommission: parseFloat(driverCommission) || 0,
          minOrderAmount: parseFloat(minOrderAmount) || 0,
          autoPayouts,
          payoutSchedule,
          defaultDeliveryFee: parseFloat(defaultDeliveryFee) || 0,
          freeDeliveryThreshold: parseFloat(freeDeliveryThreshold) || 0,
          maxDeliveryRadius: parseFloat(maxDeliveryRadius) || 0,
          estimatedDeliveryTime: parseFloat(estimatedDeliveryTime) || 0,
          requireApproval,
          allowSelfOnboarding,
          commissionRate: parseFloat(commissionRate) || 0,
          autoAcceptOrders,
          twoFactorRequired,
          sessionTimeout: parseFloat(sessionTimeout) || 30,
          ipWhitelist,
          auditLogging,
        },
      });
      setHasChanges(false);
      setSaveMessage({ kind: 'success', text: 'Settings saved.' });
    } catch (err: any) {
      setSaveMessage({ kind: 'error', text: err?.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const markChanged = () => setHasChanges(true);

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Platform Settings</h3>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">Platform Name</label>
                  <Input
                    value={platformName}
                    onChange={(e) => { setPlatformName(e.target.value); markChanged(); }}
                    placeholder="Enter platform name"
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">Support Email</label>
                  <Input
                    type="email"
                    value={supportEmail}
                    onChange={(e) => { setSupportEmail(e.target.value); markChanged(); }}
                    placeholder="support@example.com"
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">Timezone</label>
                  <select
                    value={timezone}
                    onChange={(e) => { setTimezone(e.target.value); markChanged(); }}
                    className="w-full rounded-lg border border-gray-200 p-2 sm:p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light h-9 sm:h-10"
                  >
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="pt-4 sm:pt-6 border-t border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Branding</h3>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="p-3 sm:p-4 border border-dashed border-gray-300 rounded-lg sm:rounded-xl text-center">
                  <Palette className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mx-auto mb-1 sm:mb-2" />
                  <p className="text-xs sm:text-sm text-body">Logo</p>
                  <Button variant="outline" size="sm" className="mt-1.5 sm:mt-2 h-7 sm:h-8 text-xs sm:text-sm">
                    Upload
                  </Button>
                </div>
                <div className="p-3 sm:p-4 border border-dashed border-gray-300 rounded-lg sm:rounded-xl text-center">
                  <Globe className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 mx-auto mb-1 sm:mb-2" />
                  <p className="text-xs sm:text-sm text-body">Favicon</p>
                  <Button variant="outline" size="sm" className="mt-1.5 sm:mt-2 h-7 sm:h-8 text-xs sm:text-sm">
                    Upload
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Notification Channels</h3>
              <div className="divide-y divide-gray-100">
                <Toggle
                  enabled={emailNotifications}
                  onChange={(v) => { setEmailNotifications(v); markChanged(); }}
                  label="Email Notifications"
                  description="Send notifications via email"
                />
                <Toggle
                  enabled={pushNotifications}
                  onChange={(v) => { setPushNotifications(v); markChanged(); }}
                  label="Push Notifications"
                  description="Send mobile push notifications"
                />
                <Toggle
                  enabled={smsNotifications}
                  onChange={(v) => { setSmsNotifications(v); markChanged(); }}
                  label="SMS Notifications"
                  description="Send SMS text messages (additional charges may apply)"
                />
              </div>
            </div>

            <div className="pt-4 sm:pt-6 border-t border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Alert Types</h3>
              <div className="divide-y divide-gray-100">
                <Toggle
                  enabled={orderAlerts}
                  onChange={(v) => { setOrderAlerts(v); markChanged(); }}
                  label="Order Alerts"
                  description="Get notified about new orders and status changes"
                />
                <Toggle
                  enabled={disputeAlerts}
                  onChange={(v) => { setDisputeAlerts(v); markChanged(); }}
                  label="Dispute Alerts"
                  description="Get notified when new disputes are opened"
                />
              </div>
            </div>
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Fee Structure</h3>
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">
                    <div className="flex items-center space-x-1">
                      <Percent className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>Platform Fee (%)</span>
                    </div>
                  </label>
                  <Input
                    type="number"
                    value={platformFee}
                    onChange={(e) => { setPlatformFee(e.target.value); markChanged(); }}
                    placeholder="15"
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">
                    <div className="flex items-center space-x-1">
                      <Truck className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>Driver Commission (%)</span>
                    </div>
                  </label>
                  <Input
                    type="number"
                    value={driverCommission}
                    onChange={(e) => { setDriverCommission(e.target.value); markChanged(); }}
                    placeholder="80"
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">
                    <div className="flex items-center space-x-1">
                      <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>Minimum Order Amount</span>
                    </div>
                  </label>
                  <Input
                    type="number"
                    value={minOrderAmount}
                    onChange={(e) => { setMinOrderAmount(e.target.value); markChanged(); }}
                    placeholder="25"
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="pt-4 sm:pt-6 border-t border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Payouts</h3>
              <div className="divide-y divide-gray-100">
                <Toggle
                  enabled={autoPayouts}
                  onChange={(v) => { setAutoPayouts(v); markChanged(); }}
                  label="Automatic Payouts"
                  description="Automatically process payouts on schedule"
                />
              </div>
              {autoPayouts && (
                <div className="mt-3 sm:mt-4">
                  <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">Payout Schedule</label>
                  <select
                    value={payoutSchedule}
                    onChange={(e) => { setPayoutSchedule(e.target.value); markChanged(); }}
                    className="w-full rounded-lg border border-gray-200 p-2 sm:p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light h-9 sm:h-10"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              )}
            </div>

            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-3 sm:p-4 flex items-start space-x-2 sm:space-x-3">
                <Key className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="font-medium text-heading text-sm sm:text-base">Stripe Integration</p>
                  <p className="text-xs sm:text-sm text-body mt-0.5 sm:mt-1">Connected • Live mode enabled</p>
                  <Button variant="outline" size="sm" className="mt-1.5 sm:mt-2 h-7 sm:h-8 text-xs sm:text-sm">
                    Manage Stripe
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'delivery':
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Delivery Fees</h3>
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">Default Delivery Fee ($)</label>
                  <Input
                    type="number"
                    value={defaultDeliveryFee}
                    onChange={(e) => { setDefaultDeliveryFee(e.target.value); markChanged(); }}
                    placeholder="9.99"
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">Free Delivery Threshold ($)</label>
                  <Input
                    type="number"
                    value={freeDeliveryThreshold}
                    onChange={(e) => { setFreeDeliveryThreshold(e.target.value); markChanged(); }}
                    placeholder="100"
                    className="h-9 sm:h-10 text-sm"
                  />
                  <p className="text-[10px] sm:text-xs text-body mt-0.5 sm:mt-1">Orders above this amount get free delivery</p>
                </div>
              </div>
            </div>

            <div className="pt-4 sm:pt-6 border-t border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Delivery Parameters</h3>
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">Max Delivery Radius (miles)</label>
                  <Input
                    type="number"
                    value={maxDeliveryRadius}
                    onChange={(e) => { setMaxDeliveryRadius(e.target.value); markChanged(); }}
                    placeholder="10"
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">Est. Delivery Time (min)</label>
                  <Input
                    type="number"
                    value={estimatedDeliveryTime}
                    onChange={(e) => { setEstimatedDeliveryTime(e.target.value); markChanged(); }}
                    placeholder="60"
                    className="h-9 sm:h-10 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'business':
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Onboarding</h3>
              <div className="divide-y divide-gray-100">
                <Toggle
                  enabled={requireApproval}
                  onChange={(v) => { setRequireApproval(v); markChanged(); }}
                  label="Require Admin Approval"
                  description="New businesses must be approved before going live"
                />
                <Toggle
                  enabled={allowSelfOnboarding}
                  onChange={(v) => { setAllowSelfOnboarding(v); markChanged(); }}
                  label="Self-Service Onboarding"
                  description="Allow businesses to sign up through the website"
                />
              </div>
            </div>

            <div className="pt-4 sm:pt-6 border-t border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Order Settings</h3>
              <div className="divide-y divide-gray-100">
                <Toggle
                  enabled={autoAcceptOrders}
                  onChange={(v) => { setAutoAcceptOrders(v); markChanged(); }}
                  label="Auto-Accept Orders"
                  description="Automatically accept orders on behalf of businesses"
                />
              </div>
              <div className="mt-3 sm:mt-4">
                <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">Commission Rate (%)</label>
                <Input
                  type="number"
                  value={commissionRate}
                  onChange={(e) => { setCommissionRate(e.target.value); markChanged(); }}
                  placeholder="12"
                  className="h-9 sm:h-10 text-sm"
                />
                <p className="text-[10px] sm:text-xs text-body mt-0.5 sm:mt-1">Platform commission charged to businesses</p>
              </div>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Authentication</h3>
              <div className="divide-y divide-gray-100">
                <Toggle
                  enabled={twoFactorRequired}
                  onChange={(v) => { setTwoFactorRequired(v); markChanged(); }}
                  label="Require 2FA for Admins"
                  description="All admin accounts must enable two-factor authentication"
                />
                <Toggle
                  enabled={ipWhitelist}
                  onChange={(v) => { setIpWhitelist(v); markChanged(); }}
                  label="IP Whitelist"
                  description="Restrict admin access to specific IP addresses"
                />
              </div>
              <div className="mt-3 sm:mt-4">
                <label className="block text-xs sm:text-sm font-medium text-heading mb-1 sm:mb-1.5">Session Timeout (minutes)</label>
                <Input
                  type="number"
                  value={sessionTimeout}
                  onChange={(e) => { setSessionTimeout(e.target.value); markChanged(); }}
                  placeholder="30"
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
            </div>

            <div className="pt-4 sm:pt-6 border-t border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-heading mb-3 sm:mb-4">Audit & Logging</h3>
              <div className="divide-y divide-gray-100">
                <Toggle
                  enabled={auditLogging}
                  onChange={(v) => { setAuditLogging(v); markChanged(); }}
                  label="Audit Logging"
                  description="Log all admin actions for compliance"
                />
              </div>
            </div>

            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-3 sm:p-4 flex items-start space-x-2 sm:space-x-3">
                <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="font-medium text-heading text-sm sm:text-base">API Keys</p>
                  <p className="text-xs sm:text-sm text-body mt-0.5 sm:mt-1">Manage API keys for third-party integrations</p>
                  <Button variant="outline" size="sm" className="mt-1.5 sm:mt-2 h-7 sm:h-8 text-xs sm:text-sm">
                    Manage API Keys
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif text-heading">Settings</h1>
          <p className="text-body text-sm sm:text-base mt-0.5 sm:mt-1">Manage platform configuration and preferences</p>
        </div>
        {hasChanges && (
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Button variant="outline" onClick={() => setHasChanges(false)} disabled={saving} className="h-9 sm:h-10 text-sm flex-1 sm:flex-none">
              <RefreshCcw className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Discard</span>
            </Button>
            <Button onClick={handleSave} loading={saving} className="h-9 sm:h-10 text-sm flex-1 sm:flex-none">
              <Save className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Save Changes</span>
              <span className="sm:hidden">Save</span>
            </Button>
          </div>
        )}
      </div>

      {saveMessage && (
        <div className={cn(
          "p-3 rounded-xl text-sm flex items-start gap-2",
          saveMessage.kind === 'success'
            ? "bg-green-50 border border-green-200 text-green-800"
            : "bg-red-50 border border-red-200 text-red-700"
        )}>
          {saveMessage.kind === 'success' ? <Check className="h-4 w-4 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />}
          {saveMessage.text}
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Mobile Navigation - Horizontal Scrollable */}
        <div className="lg:hidden overflow-x-auto -mx-3 sm:-mx-4 px-3 sm:px-4 pb-2">
          <div className="flex space-x-2 min-w-max">
            {settingsSections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={cn(
                    "flex items-center space-x-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap",
                    activeSection === section.id
                      ? "bg-primary text-white"
                      : "bg-gray-100 text-body hover:bg-gray-200"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Desktop Sidebar Navigation */}
        <Card className="hidden lg:block lg:col-span-1 h-fit">
          <CardContent className="p-2">
            <nav className="space-y-1">
              {settingsSections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={cn(
                      "w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors",
                      activeSection === section.id
                        ? "bg-primary text-white"
                        : "text-body hover:bg-gray-100"
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{section.label}</span>
                  </button>
                );
              })}
            </nav>
          </CardContent>
        </Card>

        {/* Content Area */}
        <Card className="lg:col-span-3">
          <CardContent className="p-3 sm:p-4 lg:p-6">
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

